'use client';

import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useRouter } from 'next/navigation';
import ChevronRight from '@mui/icons-material/ChevronRight';

import { Dialog, Text } from '@ui-library';
import { Lock } from '@ui-library/icons';
import SectionTitle from '@/components/theme/SectionTitle';
import { useKioskMode } from '@/hooks/useKioskMode';

import styles from './KioskLockedActions.module.scss';

type LockedAction = 'checkIn' | 'booking' | 'chat';

type KioskLockedActionsProps = {
  locale: string;
  company: string;
  guestBookSlug: string;
  primaryColor: string;
  hasChat: boolean;
};

export const KioskLockedActions = ({
  locale,
  company,
  guestBookSlug,
  primaryColor,
  hasChat,
}: KioskLockedActionsProps) => {
  const intl = useIntl();
  const router = useRouter();
  const { isVisitor } = useKioskMode();
  const [openAction, setOpenAction] = useState<LockedAction | null>(null);

  if (!isVisitor) {
    return null;
  }

  const portalHome = `/${locale}/${company}/${guestBookSlug}`;

  const actions: {
    key: LockedAction;
    label: string;
    prompt: string;
    redirect?: string;
  }[] = [
    {
      key: 'checkIn',
      label: intl.formatMessage({
        id: 'kiosk.locked.checkIn',
        defaultMessage: 'Check in',
      }),
      prompt: intl.formatMessage({
        id: 'kiosk.locked.checkIn.prompt',
        defaultMessage: 'Sign in to check in',
      }),
    },
    {
      key: 'booking',
      label: intl.formatMessage({
        id: 'kiosk.locked.booking',
        defaultMessage: 'My booking',
      }),
      prompt: intl.formatMessage({
        id: 'kiosk.locked.booking.prompt',
        defaultMessage: 'Sign in to see your booking',
      }),
      redirect: portalHome,
    },
    ...(hasChat
      ? [
          {
            key: 'chat' as const,
            label: intl.formatMessage({
              id: 'kiosk.locked.chat',
              defaultMessage: 'Chat with us',
            }),
            prompt: intl.formatMessage({
              id: 'kiosk.locked.chat.prompt',
              defaultMessage: 'Sign in to chat with us',
            }),
            redirect: `${portalHome}/messages`,
          },
        ]
      : []),
  ];

  const selected = actions.find((action) => action.key === openAction);

  return (
    <>
      <SectionTitle>
        {intl.formatMessage({
          id: 'kiosk.locked.title',
          defaultMessage: 'For guests with a reservation',
        })}
      </SectionTitle>
      <ul className={styles.container}>
        {actions.map((action) => (
          <li className={styles.menuItem} key={action.key}>
            <button
              type="button"
              onClick={() => setOpenAction(action.key)}
              style={{ borderColor: primaryColor, color: primaryColor }}
            >
              <Lock size={22} />
              <span className={styles.item}>{action.label}</span>
              <ChevronRight />
            </button>
          </li>
        ))}
      </ul>

      {selected && (
        <Dialog
          open
          onOpenChange={({ open }) => {
            if (!open) setOpenAction(null);
          }}
          variant="info"
          title={selected.prompt}
          size="md"
          placement="center"
          saveAction={() =>
            router.push(
              selected.redirect
                ? `/${locale}/${company}/login?redirect=${encodeURIComponent(selected.redirect)}`
                : `/${locale}/${company}/login`,
            )
          }
          cancelAction={() => setOpenAction(null)}
          cancelText={intl.formatMessage({
            id: 'kiosk.locked.keepBrowsing',
            defaultMessage: 'Keep browsing',
          })}
          saveText={intl.formatMessage({
            id: 'kiosk.locked.signIn',
            defaultMessage: 'Sign in',
          })}
        >
          <Text>
            {intl.formatMessage({
              id: 'kiosk.locked.prompt.body',
              defaultMessage:
                "We'll need your last name and reservation details. Everything else here stays open to browse.",
            })}
          </Text>
        </Dialog>
      )}
    </>
  );
};
